import { connect, type ConnectedProps } from 'react-redux';
import { type SubmitHandler } from 'react-hook-form';
import { useCallback, useEffect, useRef, useState } from 'react';
import EventForm, { type FormValues } from '@/components/EventForm';
import { type FormElement } from '@/components/Form';
import {
  currentEvent,
  currentContributors,
  editEvent,
  tryFetchEventOptions,
} from '@/slices/events';
import { fetchMembers } from '@/slices/members';
import { type RootState } from '@/store';
import { ModalLayout } from '../../layouts';
import { type ModalContentProps } from '../../../state';

type Props = ModalContentProps & ConnectedProps<typeof connector>;

const EditEventModal = ({
  closeModal,
  event,
  contributors,
  editEvent,
  tryFetchEventOptions,
  fetchMembers,
}: Props) => {
  const formRef = useRef<FormElement>(null);
  const [editorValue, setEditorValue] = useState(
    event?.placeholder_content || '',
  );

  useEffect(() => {
    tryFetchEventOptions();
    fetchMembers({ propertyId: event?.property_id || '' });
  }, [event?.property_id, tryFetchEventOptions, fetchMembers]);

  const defaultValues: FormValues = {
    name: event?.name || '',
    event_type_id: String(event?.event_type?.id) || '',
    event_status_id: String(event?.event_status?.id) || '',
    sport_id: event?.sport ? String(event.sport.id) : '',
    contributors: contributors.map((c) => c.user_id || ''),
    commentators: event?.commentators ?? '',
  };

  const handleAccept = () => {
    if (formRef.current) {
      formRef.current.submitForm();
    }
  };

  const onSubmit: SubmitHandler<FormValues> = useCallback(
    ({
      name,
      event_type_id,
      event_status_id,
      sport_id,
      contributors,
      commentators,
    }) => {
      editEvent({
        id: event?.id || '',
        property_id: event?.property_id || '',
        name,
        event_type_id: Number(event_type_id),
        event_status_id: Number(event_status_id),
        sport_id: !sport_id || event_type_id !== '1' ? null : Number(sport_id),
        placeholder_content: editorValue,
        contributors,
        commentators,
      });
      closeModal();
    },
    [closeModal, editEvent, event?.id, event?.property_id, editorValue],
  );

  return (
    <ModalLayout
      closeModal={closeModal}
      title="Edit Event"
      onAccept={handleAccept}
      acceptText="Save"
      onCancel={closeModal}
      cancelText="Cancel"
    >
      <EventForm
        ref={formRef}
        editorId="edit-event-editor"
        defaultValues={defaultValues}
        editorValue={editorValue}
        onEditorChange={setEditorValue}
        onSubmit={onSubmit}
        propertyId={event?.property_id || ''}
      />
    </ModalLayout>
  );
};

const connector = connect(
  (state: RootState) => ({
    event: currentEvent(state),
    contributors: currentContributors(state),
  }),
  {
    fetchMembers,
    tryFetchEventOptions,
    editEvent,
  },
);

export default connector(EditEventModal);
